import { Box } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { brainTokens } from "../design/brainTheme";

const c = brainTokens.color;

/**
 * Multi-select twin of `Segmented` — Jira quick-filter behaviour: every option
 * toggles on and off independently, and the caller ORs the selection together.
 *
 * `clearKey` (e.g. "all") is the escape hatch: it reads as active exactly when
 * nothing else is selected, and picking it clears the rest. That keeps "no
 * filter" a real, clickable state instead of something you reach by unticking
 * chips one at a time.
 */
export function SegmentedMulti<T extends string>({
  value,
  onChange,
  options,
  clearKey,
  sx,
}: {
  value: T[];
  onChange: (v: T[]) => void;
  options: { key: T; label: string }[];
  clearKey?: T;
  sx?: SxProps<Theme>;
}) {
  const toggle = (key: T) => {
    if (clearKey && key === clearKey) {
      onChange([]);
      return;
    }
    onChange(value.includes(key) ? value.filter((x) => x !== key) : [...value, key]);
  };

  return (
    <Box
      role="group"
      sx={{
        display: "inline-flex",
        background: c.borderLight3,
        borderRadius: "8px",
        padding: "3px",
        gap: "2px",
        flexWrap: "wrap",
        ...sx,
      }}
    >
      {options.map((o) => {
        const active = clearKey && o.key === clearKey ? value.length === 0 : value.includes(o.key);
        return (
          <Box
            key={o.key}
            component="button"
            type="button"
            aria-pressed={active}
            onClick={() => toggle(o.key)}
            sx={{
              border: "none",
              fontFamily: brainTokens.font.sans,
              padding: "6px 14px",
              borderRadius: "6px",
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
              whiteSpace: "nowrap",
              color: active ? c.ink : "#7a828a",
              background: active ? "#fff" : "transparent",
              boxShadow: active ? "0 1px 2px rgba(0,0,0,.06)" : "none",
              "&:hover": { color: c.ink },
            }}
          >
            {o.label}
          </Box>
        );
      })}
    </Box>
  );
}
